import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Icons } from '../constants';
import ArboristChat from './ArboristChat';

const FAQ_DATA = [
  {
    question: "How does GreenTree support ISA Certification?",
    answer: "We cover 100% of the exam fees and provide official ISA study materials after 1 year of continuous employment. We also host monthly study groups led by our current Certified Arborists."
  },
  {
    question: "Do I need a CDL to advance?",
    answer: "While not strictly required for all roles, a Class B CDL is highly recommended and necessary for driving our bucket trucks and grapple trucks. We offer paid training time and cover the testing fees for your CDL."
  },
  {
    question: "What is the gear and boot allowance?",
    answer: "Employees receive a $500 annual allowance for boots and climbing gear after completing their 90-day probationary period. Gear must meet ANSI Z133 safety standards."
  },
  {
    question: "How do I transition from Groundworker to Climber?",
    answer: "The transition involves completing our in-house Aerial Rescue training, mastering basic knots (Bowline, Blake's Hitch, etc.), and demonstrating proficiency in SRT/DdRT basics under the supervision of a Crew Leader."
  },
  {
    question: "What are the ANSI Z133 safety standards?",
    answer: "ANSI Z133 is the safety standard for arboricultural operations. It covers everything from PPE requirements and electrical hazards to safe chainsaw operation and rigging practices. All crew members must adhere to these standards."
  }
];

const REFERENCES = [
  {
    title: "ISA Credentialing & Certification",
    url: "https://www.isa-arbor.com/Credentials",
    description: "Official information on becoming an ISA Certified Arborist, Tree Worker, or Specialist."
  },
  {
    title: "TCIA Safety Training",
    url: "https://www.tcia.org/TCIA/Safety/TCIA/Safety/Safety.aspx",
    description: "Resources for tree care safety, including the Tree Care Academy and EHAP training."
  },
  {
    title: "ANSI Z133 Safety Standards",
    url: "https://wwv.isa-arbor.com/store/product/122/",
    description: "The industry safety standard for arboricultural operations."
  },
  {
    title: "Tree Care Industry Magazine",
    url: "https://tcimag.tcia.org/",
    description: "The official publication of the TCIA, featuring articles on business, safety, and arboriculture techniques."
  }
];

const EmployeeTrainingView: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [interactiveStep, setInteractiveStep] = useState(0);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="flex flex-col lg:flex-row gap-8 max-w-7xl mx-auto p-6">
      {/* Main Content Area */}
      <div className="lg:w-2/3 space-y-8">
        
        {/* Header */}
        <div className="bg-bark p-8 rounded-[32px] text-white shadow-xl relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="text-3xl font-black mb-2">GreenTree Academy</h2>
            <p className="text-stone-400 font-medium">Your pathway to career advancement, safety mastery, and arboriculture expertise.</p>
          </div>
          <div className="absolute right-0 top-0 opacity-10 transform translate-x-10 -translate-y-10 scale-150 pointer-events-none text-gold">
            <Icons.Education />
          </div>
        </div>

        {/* Interactive Career Pathway */}
        <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
          <h3 className="text-2xl font-black text-bark mb-6 flex items-center gap-3">
            <span className="w-10 h-10 bg-gold/10 text-gold rounded-xl flex items-center justify-center">
              <Icons.Tree />
            </span>
            Career Pathway
          </h3>
          
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div className="w-full md:w-1/2 relative p-4">
              {/* Vertical Line */}
              <div className="absolute left-8 top-8 bottom-8 w-1 bg-stone-100 rounded-full z-0" />
              
              <div className="space-y-6 relative z-10">
                {[
                  { title: 'Groundworker', icon: '🪓' },
                  { title: 'Climber Trainee', icon: '🧗' },
                  { title: 'Crew Leader', icon: '👷' },
                  { title: 'ISA Certified Arborist', icon: '🎓' }
                ].map((step, idx) => (
                  <button
                    key={idx}
                    onClick={() => setInteractiveStep(idx)}
                    className={`flex items-center gap-4 w-full text-left p-3 rounded-2xl transition-all ${
                      interactiveStep === idx ? 'bg-stone-50 shadow-sm ring-2 ring-gold' : 'hover:bg-stone-50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl shadow-sm ${
                      interactiveStep === idx ? 'bg-gold text-bark' : 'bg-white border border-stone-200'
                    }`}>
                      {step.icon}
                    </div>
                    <span className={`font-bold ${interactiveStep === idx ? 'text-bark' : 'text-stone-500'}`}>
                      {step.title}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="w-full md:w-1/2">
              <motion.div 
                key={interactiveStep}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-stone-50 p-6 rounded-2xl border border-stone-200 h-full"
              >
                {interactiveStep === 0 && (
                  <>
                    <h4 className="font-black text-bark text-xl mb-2">Groundworker</h4>
                    <p className="text-sm text-stone-500 mb-4">The foundation of every crew. Learn the ropes, safety protocols, and equipment operation.</p>
                    <ul className="space-y-2 text-sm font-medium text-stone-600">
                      <li className="flex items-center gap-2"><span className="text-gecko">✓</span> Chipper & Chainsaw Safety</li>
                      <li className="flex items-center gap-2"><span className="text-gecko">✓</span> Basic Knots (Bowline, Clove Hitch)</li>
                      <li className="flex items-center gap-2"><span className="text-gecko">✓</span> Job Site Setup & Cleanup</li>
                      <li className="flex items-center gap-2"><span className="text-gecko">✓</span> CPR & First Aid Certification</li>
                    </ul>
                  </>
                )}
                {interactiveStep === 1 && (
                  <>
                    <h4 className="font-black text-bark text-xl mb-2">Climber Trainee</h4>
                    <p className="text-sm text-stone-500 mb-4">Take to the canopy. Master climbing techniques and aerial operations under supervision.</p>
                    <ul className="space-y-2 text-sm font-medium text-stone-600">
                      <li className="flex items-center gap-2"><span className="text-gecko">✓</span> Aerial Rescue Training</li>
                      <li className="flex items-center gap-2"><span className="text-gecko">✓</span> SRT & DdRT Climbing Systems</li>
                      <li className="flex items-center gap-2"><span className="text-gecko">✓</span> Proper Pruning Cuts (ANSI A300)</li>
                      <li className="flex items-center gap-2"><span className="text-gecko">✓</span> Basic Rigging Techniques</li>
                    </ul>
                  </>
                )}
                {interactiveStep === 2 && (
                  <>
                    <h4 className="font-black text-bark text-xl mb-2">Crew Leader</h4>
                    <p className="text-sm text-stone-500 mb-4">Lead the team. Manage job sites, ensure safety compliance, and handle complex rigging.</p>
                    <ul className="space-y-2 text-sm font-medium text-stone-600">
                      <li className="flex items-center gap-2"><span className="text-gecko">✓</span> Advanced Rigging & Felling</li>
                      <li className="flex items-center gap-2"><span className="text-gecko">✓</span> Job Hazard Analysis (JHA)</li>
                      <li className="flex items-center gap-2"><span className="text-gecko">✓</span> Client Communication</li>
                      <li className="flex items-center gap-2"><span className="text-gecko">✓</span> Equipment Maintenance Leadership</li>
                    </ul>
                  </>
                )}
                {interactiveStep === 3 && (
                  <>
                    <h4 className="font-black text-bark text-xl mb-2">ISA Certified Arborist</h4>
                    <p className="text-sm text-stone-500 mb-4">The industry gold standard. Provide expert consulting, diagnosis, and plant health care.</p>
                    <ul className="space-y-2 text-sm font-medium text-stone-600">
                      <li className="flex items-center gap-2"><span className="text-gecko">✓</span> Tree Biology & Identification</li>
                      <li className="flex items-center gap-2"><span className="text-gecko">✓</span> Soil Management & Nutrition</li>
                      <li className="flex items-center gap-2"><span className="text-gecko">✓</span> Tree Risk Assessment</li>
                      <li className="flex items-center gap-2"><span className="text-gecko">✓</span> Plant Health Care (PHC)</li>
                    </ul>
                  </>
                )}
              </motion.div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
          <h3 className="text-2xl font-black text-bark mb-6">Career & Training FAQ</h3>
          <div className="space-y-4">
            {FAQ_DATA.map((faq, index) => (
              <div key={index} className="border border-stone-200 rounded-2xl overflow-hidden">
                <button 
                  onClick={() => toggleFaq(index)}
                  className="w-full text-left p-5 bg-stone-50 hover:bg-stone-100 transition flex justify-between items-center"
                >
                  <span className="font-bold text-bark">{faq.question}</span>
                  <span className="text-stone-400 font-black text-xl">{openFaq === index ? '−' : '+'}</span>
                </button>
                {openFaq === index && (
                  <div className="p-5 bg-white text-stone-600 text-sm leading-relaxed border-t border-stone-200">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* References & Links */}
        <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
          <h3 className="text-2xl font-black text-bark mb-6">Industry Resources</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {REFERENCES.map((ref, index) => (
              <a 
                key={index}
                href={ref.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-5 rounded-2xl border border-stone-200 hover:border-palm hover:shadow-md transition group"
              >
                <h4 className="font-bold text-bark group-hover:text-palm transition mb-2 flex items-center gap-2">
                  {ref.title}
                  <span className="text-stone-300 group-hover:text-palm">↗</span>
                </h4>
                <p className="text-xs text-stone-500 leading-relaxed">{ref.description}</p>
              </a>
            ))}
          </div>
        </div>

      </div>

      {/* Sidebar: Arb-E Monkey AI (Employee Mode) */}
      <div className="lg:w-1/3">
        <div className="sticky top-8 h-[calc(100vh-4rem)]">
          <ArboristChat mode="employee" />
        </div>
      </div>
    </div>
  );
};

export default EmployeeTrainingView;
